import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as debounce from 'lodash.debounce';
import QuickSearchResult from './QuickSearchResult';
import { translate } from '../Services/translation';
import {
    query,
    setSearchQuery,
    toggleShowFullForm,
    handleKeyDown,
    handleClickSearch
} from '../Actions/QuickSearch.action';
import usePrevious from '@react-hook/previous';

// debouncing function to 200ms so we don't send query request on every key stroke
const debouncedQuery = debounce((dispatch, text) => dispatch(query(text)), 200);

const QuickSearch = () => {
    const {
        query,
        result,
        showResult,
        showFullForm,
        selectedItem,
    } = useSelector((state) => state.quickSearch);
    const lastClickedNode = useRef(null);
    const searchUrl =
        window.__litium.quickSearchUrl +
        (query.length > 0 ? `?q=${query}` : '');
    const searchContainer = useRef(null);
    const searchInput = useRef(null);
    const dispatch = useDispatch();
    const previousSelectedItem = usePrevious(selectedItem);

    const clickHandler = useCallback((event) => {
        lastClickedNode.current = event.target;
    }, []);
    useEffect(() => {
        // listen for click event to hide the search when clicking outside
        window.addEventListener('mousedown', clickHandler);
        return () => window.removeEventListener('mousedown', clickHandler);
    }, [clickHandler]);

    useEffect(() => {
        // set query value if it is avaialble in the Url
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.has('q')) {
            const query = urlParams.get('q');
            dispatch(setSearchQuery(query));
        }
    }, [dispatch]);

    useEffect(() => {
        if (selectedItem !== previousSelectedItem) {
            const el = document.querySelector(
                '.quick-search-result__item--selected'
            );
            el &&
                el.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end',
                    inline: 'nearest',
                });
        }
    }, [selectedItem, previousSelectedItem]);

    return (
        <div className="quick-search quick-search--green" role="search" ref={searchContainer}>
            <a
                className="quick-search__link--block"
                onClick={(e) => {
                    dispatch(toggleShowFullForm());
                    setTimeout(() => {
                        searchInput.current && searchInput.current.focus();
                    }, 0);
                }}
                aria-label={translate('general.search')}
            >
                
                <svg width='19' height='19' xmlns='http://www.w3.org/2000/svg'><path d='M13.749 12.217h-.87l-.307-.297a7.142 7.142 0 0 0 1.627-5.87C13.683 2.995 11.131.554 8.051.18A7.152 7.152 0 0 0 .056 8.173C.43 11.249 2.87 13.8 5.929 14.316a7.152 7.152 0 0 0 5.873-1.627l.297.308v.868l4.674 4.672c.451.45 1.188.45 1.639 0a1.16 1.16 0 0 0 0-1.638l-4.663-4.682Zm-6.6 0A4.941 4.941 0 0 1 2.2 7.27a4.941 4.941 0 0 1 4.95-4.946 4.942 4.942 0 0 1 4.949 4.946 4.941 4.941 0 0 1-4.95 4.947Z' fillRule='evenodd'/></svg>
                <span className="quick-search__title">
                    {translate('general.search')}
                </span>
            </a>
            <div
                className={`quick-search__form ${
                    showFullForm ? 'quick-search__form--force-show' : ''
                }`}
                role="search"
            >
                <i
                    className="quick-search__icon"
                    onClick={(e) => dispatch(toggleShowFullForm())}
                ></i>
                                <button
                    className="quick-search__submit-button"
                    type="button"
                    aria-label={translate('general.search')}
                    onClick={(e) => {
                        e.preventDefault();
                        dispatch(handleClickSearch({ searchUrl }));
                    }}
                >
                    <svg width='19' height='19' xmlns='http://www.w3.org/2000/svg'><path d='M13.749 12.217h-.87l-.307-.297a7.142 7.142 0 0 0 1.627-5.87C13.683 2.995 11.131.554 8.051.18A7.152 7.152 0 0 0 .056 8.173C.43 11.249 2.87 13.8 5.929 14.316a7.152 7.152 0 0 0 5.873-1.627l.297.308v.868l4.674 4.672c.451.45 1.188.45 1.639 0a1.16 1.16 0 0 0 0-1.638l-4.663-4.682Zm-6.6 0A4.941 4.941 0 0 1 2.2 7.27a4.941 4.941 0 0 1 4.95-4.946 4.942 4.942 0 0 1 4.949 4.946 4.941 4.941 0 0 1-4.95 4.947Z' fillRule='evenodd'/></svg>
                    <span className="quick-search__submit-title">
                        {translate('general.search')}
                    </span>
                </button>
                <input
                    className="quick-search__input"
                    type="search"
                    placeholder={translate('general.search')}
                    autoComplete="off"
                    value={decodeURIComponent(query)}
                    onChange={(event) => {
                        const text = encodeURIComponent(event.target.value);
                        dispatch(setSearchQuery(text));
                        debouncedQuery(dispatch, text);
                    }}
                    onKeyDown={(event) =>
                        dispatch(handleKeyDown(event, { searchUrl }))
                    }
                    ref={searchInput}
                    onBlur={() => {
                        if (
                            searchContainer.current &&
                            !searchContainer.current.contains(lastClickedNode.current)
                        ) {
                            showFullForm && dispatch(toggleShowFullForm());
                        }
                    }}
                />

                {showResult && (
                    <QuickSearchResult
                        result={result}
                        selectedItem={selectedItem}
                        searchUrl={searchUrl}
                    />
                )}
            </div>
        </div>
    );
};

export default QuickSearch;
